import React from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthentication,
  useMarkNotificationsAsRead,
  useRetrieveNotifications,
} from "@planzone/view-models";

import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Notification } from "@scenes/notifications/notification";
import { Loader } from "@scenes/notifications/loader";
import { ToolBar } from "./toolBar";
import { Props } from "./types";
import styles from "./styles.scss";

const PAGE_LIMIT = 20;

function NotificationsPage(_: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    filter,
    notifications,
    totalPages,
    retrieveNotifications,
  } = useRetrieveNotifications();
  const { markNotificationsAsRead } = useMarkNotificationsAsRead();
  const [selectedTypes, setSelectedTypes] = React.useState<string[]>([]);
  const [hasFilterChanged, setHasFilterChanged] = React.useState(false);

  React.useEffect(() => {
    retrieveNotifications({
      filter: {
        limit: PAGE_LIMIT,
        start: 0,
        types: selectedTypes.length > 0 ? selectedTypes : undefined,
      },
      resource: account.resource,
    });
    markNotificationsAsRead([]);
  }, []);

  React.useEffect(() => {
    if (hasFilterChanged) {
      retrieveNotifications({
        filter: {
          limit: PAGE_LIMIT,
          start: 0,
          types: selectedTypes.length > 0 ? selectedTypes : undefined,
        },
        resource: account.resource,
      });
      setHasFilterChanged(false);
    }
  }, [hasFilterChanged]);

  return (
    <div className={styles.notifications}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("notificationsPage.title")}</h1>
        <div className={styles.profileNavigation}>
          <AssistantShortcut />
          <Notifications />
          <ProfileNavigation />
        </div>
      </div>
      <div className={styles.main}>
        <ToolBar
          selectedTypes={selectedTypes}
          setSelectedTypes={(types) => {
            setSelectedTypes(types);
            setHasFilterChanged(true);
          }}
        />
        <InfiniteScroll
          canFetchOnIdle={
            !isRequestPending &&
            !isRequestSuccess &&
            !isRequestFailure &&
            notifications.length === 0
          }
        >
          {() => (
            <div className={styles.list}>
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div key={notification.uuid} className={styles.item}>
                    <Notification notification={notification} />
                  </div>
                ))
              ) : isRequestPending ? (
                <>
                  <Loader />
                  <Loader />
                  <Loader />
                  <Loader />
                </>
              ) : (
                <p className={styles.placeholder}>
                  {t("notifications.placeholder")}
                </p>
              )}
              <Flag
                name="notificationsFlag"
                retrieveItems={() =>
                  !isRequestPending &&
                  retrieveNotifications({
                    filter: {
                      ...filter,
                      types:
                        selectedTypes.length > 0 ? selectedTypes : undefined,
                    },
                    resource: account.resource,
                  })
                }
                isDisabled={
                  isRequestPending ||
                  (filter.start > 0 &&
                    totalPages > 0 &&
                    filter.start >= totalPages)
                }
              />
            </div>
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
}

export { NotificationsPage };
